import React from 'react';

const NiraVerificationDialog = ({ onClose, onSubmit, initialValues, hideActions = false, readOnly = false, title }) => {
  const [form, setForm] = React.useState({
    nationalId: '',
    documentId: '',
    surname: '',
    givenNames: '',
    otherNames: '',
    dateOfBirth: '', // DD/MM/YYYY for submission
    dobIso: '', // YYYY-MM-DD for date picker
  });
  const [errors, setErrors] = React.useState({});
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (initialValues) {
      const toDdMmYyyy = (val) => {
        if (!val) return '';
        // Accept already formatted DD/MM/YYYY
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(val)) return val;
        // Convert YYYY-MM-DD to DD/MM/YYYY
        const m = String(val).match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m) return `${m[3]}/${m[2]}/${m[1]}`;
        return String(val);
      };
      const rawDob = initialValues.dateOfBirth || '';
      const ddmmyyyy = toDdMmYyyy(rawDob);
      const toIso = (val) => {
        if (!val) return '';
        // If already ISO
        if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;
        // If DD/MM/YYYY convert to YYYY-MM-DD
        const m = String(val).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (m) return `${m[3]}-${m[2]}-${m[1]}`;
        return '';
      };
      setForm({
        nationalId: initialValues.nationalId || '',
        documentId: initialValues.documentId || '',
        surname: initialValues.surname || '',
        givenNames: initialValues.givenNames || '',
        otherNames: initialValues.otherNames || '',
        dateOfBirth: ddmmyyyy,
        dobIso: toIso(rawDob || ddmmyyyy),
      });
    }
  }, [initialValues]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'dobIso') {
      const iso = value;
      const toDd = (v) => {
        if (!v) return '';
        const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (m) return `${m[3]}/${m[2]}/${m[1]}`;
        return '';
      };
      setForm((prev) => ({ ...prev, dobIso: iso, dateOfBirth: toDd(iso) }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    if (readOnly || hideActions) return true;
    const errs = {};
    if (!form.nationalId?.trim()) errs.nationalId = 'National Id is required';
    if (!form.documentId?.trim()) errs.documentId = 'Document Id is required';

    const anyOptional = [form.surname, form.givenNames, form.otherNames, form.dateOfBirth].some((v) => (v || '').trim());
    if (!anyOptional) errs.anyOptional = 'Provide at least one of: Date of Birth, Surname, Given Names, Other Names';

    if (readOnly) {
    } else {
      if (form.dobIso?.trim()) {
        const isoOk = /^\d{4}-\d{2}-\d{2}$/.test(form.dobIso.trim());
        if (!isoOk) errs.dateOfBirth = 'Invalid date';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    try {
      setSubmitting(true);
      await onSubmit?.({
        nationalId: form.nationalId.trim(),
        documentId: form.documentId.trim(),
        surname: form.surname?.trim() || undefined,
        givenNames: form.givenNames?.trim() || undefined,
        otherNames: form.otherNames?.trim() || undefined,
        // Always submit DD/MM/YYYY as required by backend service
        dateOfBirth: form.dateOfBirth?.trim() || undefined,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="react-confirm-alert">
      <div
        className="react-confirm-alert-overlay"
        onClick={() => {
          if (!submitting) onClose?.();
        }}
      >
        <div
          className="react-confirm-alert-body"
          style={{ maxWidth: 520, width: '100%', position: 'relative' }}
          onClick={(e) => e.stopPropagation()}
        >
          {submitting && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(0, 0, 0, 0.45)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2,
                borderRadius: 'var(--border-radius)'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 10,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 16
                }}
              >
                <i className="fas fa-spinner fa-spin" style={{ fontSize: 28 }}></i>
                Submitting request...
              </div>
            </div>
          )}
        <h3 style={{ marginBottom: 12 }}><i className="fas fa-id-card"></i> {title || (hideActions || readOnly ? 'View Verification (NIRA)' : 'Verify Identity (NIRA)')}</h3>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>National Id</label>
            <input name="nationalId" value={form.nationalId} onChange={handleChange} placeholder="e.g. CF1234567ABCD" disabled={readOnly || submitting} />
            <small>National Identification Number as it appears on the card</small>
            {errors.nationalId && <div className="error-text">{errors.nationalId}</div>}
          </div>
          <div className="form-group">
            <label>Document Id</label>
            <input name="documentId" value={form.documentId} onChange={handleChange} placeholder="Card Number as on the card" disabled={readOnly || submitting} />
            <small>National ID Card Number as it appears on the card</small>
            {errors.documentId && <div className="error-text">{errors.documentId}</div>}
          </div>
          <div className="form-group">
            <label>Surname</label>
            <input name="surname" value={form.surname} onChange={handleChange} placeholder="Surname as on the card" disabled={readOnly || submitting} />
          </div>
          <div className="form-group">
            <label>Given Names</label>
            <input name="givenNames" value={form.givenNames} onChange={handleChange} placeholder="Given Names as on the card" disabled={readOnly || submitting} />
          </div>
          <div className="form-group">
            <label>Other Names</label>
            <input name="otherNames" value={form.otherNames} onChange={handleChange} placeholder="Other Names as on the card" disabled={readOnly || submitting} />
          </div>
          <div className="form-group">
            <label>Date Of Birth</label>
            {readOnly ? (
              <input name="dateOfBirth" value={form.dateOfBirth} onChange={handleChange} placeholder="DD/MM/YYYY" disabled />
            ) : (
              <input type="date" name="dobIso" value={form.dobIso} onChange={handleChange} disabled={submitting} />
            )}
            {errors.dateOfBirth && <div className="error-text">{errors.dateOfBirth}</div>}
          </div>
          {errors.anyOptional && <div className="error-text" style={{ marginTop: 4 }}>{errors.anyOptional}</div>}

          {!hideActions && (
            <>
              <div className="react-confirm-alert-button-group" style={{ marginTop: 16 }}>
                <button type="button" className="btn-secondary" onClick={onClose} disabled={submitting}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? (<><i className="fas fa-spinner fa-spin"></i> Submitting...</>) : (<><i className="fas fa-check"></i> Submit</>)}
                </button>
              </div>
              <div style={{ marginTop: 8 }}>
                <small>For verification you need to additionally provide at least one of: date of birth, surname, given names, other names.</small>
              </div>
            </>
          )}
        </form>
        </div>
      </div>
    </div>
  );
};

export default NiraVerificationDialog;
